import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../app/store'
import createDebouncedAsyncThunk from '../../shared/utils/async'
import { mpTrackSearchText } from '../../tracking/mixpanel'
import { transformArticleFromResponse } from '../article/transforms'
import { IArticleTransformed } from '../article/types/IArticleTransformed'
import { fetchArticlesBySearch } from './searchApi'

export interface SearchText {
    searchText: string
    searchedArticles: IArticleTransformed[]
    searchedArticlesStatus: 'pending' | 'idle' | 'failed'
}

const initialState: SearchText = {
    searchText: '',
    searchedArticles: [],
    searchedArticlesStatus: 'idle',
}

export const fetchArticlesBySearchThunk = createDebouncedAsyncThunk(
    'article/fetchArticlesBySearch',
    async (payload: { text: string; publisherKey: string; currentlyPlayingArticle?: IArticleTransformed }) => {
        mpTrackSearchText(payload.text, payload.currentlyPlayingArticle)

        const response = await fetchArticlesBySearch({
            publisherKey: payload.publisherKey,
            text: payload.text,
        })

        return response
    },
    1000
)

export const searchSlice = createSlice({
    name: 'search',
    initialState,
    reducers: {
        setSearchText: (state, action: PayloadAction<string>) => {
            state.searchText = action.payload
        },
        clearSearchedArticles: state => {
            state.searchedArticles = []
        },
        setSearchedArticlesStatusToLoading: state => {
            state.searchedArticlesStatus = 'pending'
        },
    },

    extraReducers: builder => {
        builder
            .addCase(fetchArticlesBySearchThunk.pending, state => {
                state.searchedArticlesStatus = 'pending'
            })
            .addCase(fetchArticlesBySearchThunk.fulfilled, (state, action) => {
                const articlesTransformed = (action.payload as any).map(transformArticleFromResponse)

                state.searchedArticlesStatus = 'idle'
                state.searchedArticles = articlesTransformed
            })
            .addCase(fetchArticlesBySearchThunk.rejected, state => {
                state.searchedArticlesStatus = 'failed'
            })
    },
})

export const { setSearchText, clearSearchedArticles, setSearchedArticlesStatusToLoading } = searchSlice.actions
export const selectSearchText = (state: RootState) => state.search.searchText
export const selectSearchedArticles = (state: RootState) => state.search.searchedArticles
export const selectSearchedArticlesStatus = (state: RootState) => state.search.searchedArticlesStatus

export default searchSlice.reducer
